import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth.js';
import { ReviewCountProvider, useReviewCount } from './contexts/ReviewCountContext.js';
import { RenewalTopBar } from './renewal-theme';
import { AppSidebar } from './components/AppSidebar.js';
import './App.css';

// Eagerly loaded pages (needed immediately)
import { Login } from './pages/Login.js';
import { Callback } from './pages/Callback.js';
import { Timesheet } from './pages/Timesheet.js';

// Lazy loaded pages (supervisor features, loaded on demand)
const EmployeeList = lazy(() =>
  import('./pages/EmployeeList.js').then((m) => ({ default: m.EmployeeList }))
);
const EmployeeDetail = lazy(() =>
  import('./pages/EmployeeDetail.js').then((m) => ({ default: m.EmployeeDetail }))
);
const TaskCodeList = lazy(() =>
  import('./pages/TaskCodeList.js').then((m) => ({ default: m.TaskCodeList }))
);
const TaskCodeDetail = lazy(() =>
  import('./pages/TaskCodeDetail.js').then((m) => ({ default: m.TaskCodeDetail }))
);
const TaskCodeForm = lazy(() =>
  import('./components/TaskCodeForm.js').then((m) => ({ default: m.TaskCodeForm }))
);
// Rate audit detail + refresh wizard — drill-downs reached only FROM a task code
// (the unification made the task code the one home for rates; there is no
// standalone Rates list or create-a-loose-rate form anymore).
const RateDetail = lazy(() =>
  import('./pages/RateDetail.js').then((m) => ({ default: m.RateDetail }))
);
const RateRefreshWizard = lazy(() =>
  import('./pages/RateRefreshWizard.js').then((m) => ({ default: m.RateRefreshWizard }))
);
const VolunteerList = lazy(() =>
  import('./pages/VolunteerList.js').then((m) => ({ default: m.VolunteerList }))
);
const VolunteerDetail = lazy(() =>
  import('./pages/VolunteerDetail.js').then((m) => ({ default: m.VolunteerDetail }))
);
const VolunteerForm = lazy(() =>
  import('./components/VolunteerForm.js').then((m) => ({ default: m.VolunteerForm }))
);
const OnboardVolunteerWizard = lazy(() =>
  import('./pages/OnboardVolunteerWizard.js').then((m) => ({ default: m.OnboardVolunteerWizard }))
);
const ReviewQueue = lazy(() =>
  import('./pages/ReviewQueue.js').then((m) => ({ default: m.ReviewQueue }))
);
const ReviewDetail = lazy(() =>
  import('./pages/ReviewDetail.js').then((m) => ({ default: m.ReviewDetail }))
);
const PayrollReportPage = lazy(() =>
  import('./pages/PayrollReportPage.js').then((m) => ({ default: m.PayrollReportPage }))
);
const Alerts = lazy(() => import('./pages/Alerts.js').then((m) => ({ default: m.Alerts })));
const ReportsDashboard = lazy(() =>
  import('./pages/ReportsDashboard.js').then((m) => ({ default: m.ReportsDashboard }))
);
const MatchBankSummaryReport = lazy(() =>
  import('./pages/MatchBankSummaryReport.js').then((m) => ({ default: m.MatchBankSummaryReport }))
);
const ComplianceAuditReport = lazy(() =>
  import('./pages/ComplianceAuditReport.js').then((m) => ({ default: m.ComplianceAuditReport }))
);
const TimesheetHistoryReport = lazy(() =>
  import('./pages/TimesheetHistoryReport.js').then((m) => ({ default: m.TimesheetHistoryReport }))
);

// Employee report pages (accessible to all authenticated users)
const MyReportsDashboard = lazy(() =>
  import('./pages/MyReportsDashboard.js').then((m) => ({ default: m.MyReportsDashboard }))
);
const MyTimesheetHistory = lazy(() =>
  import('./pages/MyTimesheetHistory.js').then((m) => ({ default: m.MyTimesheetHistory }))
);

// Volunteer self-entry (Phase 4 — accessible to authenticated users)
const MyVolunteerHours = lazy(() =>
  import('./pages/MyVolunteerHours.js').then((m) => ({ default: m.MyVolunteerHours }))
);
const MyVolunteerTimesheet = lazy(() =>
  import('./pages/MyVolunteerTimesheet.js').then((m) => ({ default: m.MyVolunteerTimesheet }))
);

// Admin batch transcription + sign-in template (Phase 4 — supervisor-only)
const SigninSheetTranscribe = lazy(() =>
  import('./pages/SigninSheetTranscribe.js').then((m) => ({ default: m.SigninSheetTranscribe }))
);
const SigninSheetTemplate = lazy(() =>
  import('./pages/SigninSheetTemplate.js').then((m) => ({ default: m.SigninSheetTemplate }))
);

// Volunteer approval (Phase 5 — supervisor-only)
const VolunteerApprovalQueue = lazy(() =>
  import('./pages/VolunteerApprovalQueue.js').then((m) => ({
    default: m.VolunteerApprovalQueue,
  }))
);
const VolunteerApprovalDetail = lazy(() =>
  import('./pages/VolunteerApprovalDetail.js').then((m) => ({
    default: m.VolunteerApprovalDetail,
  }))
);

// Kiosk admin (Phase 7 Task 4 — supervisor-only)
const KioskWaivers = lazy(() =>
  import('./pages/KioskWaivers.js').then((m) => ({ default: m.KioskWaivers }))
);
const KioskPendingCompletion = lazy(() =>
  import('./pages/KioskPendingCompletion.js').then((m) => ({
    default: m.KioskPendingCompletion,
  }))
);

// Phase 8 — audit log + fiscal-period admin surfaces
const AuditLog = lazy(() => import('./pages/AuditLog.js').then((m) => ({ default: m.AuditLog })));
const FiscalPeriods = lazy(() =>
  import('./pages/FiscalPeriods.js').then((m) => ({ default: m.FiscalPeriods }))
);
const FiscalPeriodCloseWizard = lazy(() =>
  import('./pages/FiscalPeriodCloseWizard.js').then((m) => ({
    default: m.FiscalPeriodCloseWizard,
  }))
);

// Kiosk subtree (Phase 7 — anonymous, separately themed)
const KioskApp = lazy(() => import('./kiosk/KioskApp.js').then((m) => ({ default: m.KioskApp })));
const KioskHome = lazy(() =>
  import('./kiosk/pages/KioskHome.js').then((m) => ({ default: m.KioskHome }))
);
const KioskEmail = lazy(() =>
  import('./kiosk/pages/KioskEmail.js').then((m) => ({ default: m.KioskEmail }))
);
const KioskName = lazy(() =>
  import('./kiosk/pages/KioskName.js').then((m) => ({ default: m.KioskName }))
);
const KioskWaiver = lazy(() =>
  import('./kiosk/pages/KioskWaiver.js').then((m) => ({ default: m.KioskWaiver }))
);
const KioskCheckin = lazy(() =>
  import('./kiosk/pages/KioskCheckin.js').then((m) => ({ default: m.KioskCheckin }))
);
const KioskCheckout = lazy(() =>
  import('./kiosk/pages/KioskCheckout.js').then((m) => ({ default: m.KioskCheckout }))
);

// Phase 9a — admin dashboard + reports
const AdminDashboard = lazy(() =>
  import('./pages/AdminDashboard.js').then((m) => ({ default: m.AdminDashboard }))
);
const Asu202007Report = lazy(() =>
  import('./pages/Asu202007Report.js').then((m) => ({ default: m.Asu202007Report }))
);
const RateRefreshLogReport = lazy(() =>
  import('./pages/RateRefreshLogReport.js').then((m) => ({ default: m.RateRefreshLogReport }))
);
const AuditPackagesReport = lazy(() =>
  import('./pages/AuditPackagesReport.js').then((m) => ({ default: m.AuditPackagesReport }))
);

// Grants (Phase 6 — supervisor/admin only)
const GrantsList = lazy(() =>
  import('./pages/GrantsList.js').then((m) => ({ default: m.GrantsList }))
);
const GrantSetupWizard = lazy(() =>
  import('./pages/GrantSetupWizard.js').then((m) => ({ default: m.GrantSetupWizard }))
);
const GrantDetail = lazy(() =>
  import('./pages/GrantDetail.js').then((m) => ({ default: m.GrantDetail }))
);
const MatchBank = lazy(() =>
  import('./pages/MatchBank.js').then((m) => ({ default: m.MatchBank }))
);

// Volunteer GL push (Phase 11a — admin "Processor" surface)
const VolunteerGlPush = lazy(() =>
  import('./pages/VolunteerGlPush.js').then((m) => ({ default: m.VolunteerGlPush }))
);

// CIP → Buildings transfer (Phase 11b Task 6 — admin one-time transfer)
const CipTransfer = lazy(() =>
  import('./pages/CipTransfer.js').then((m) => ({ default: m.CipTransfer }))
);
// Phase 11c — GL reconciliation admin surface
const Reconciliation = lazy(() =>
  import('./pages/Reconciliation.js').then((m) => ({ default: m.Reconciliation }))
);
const ReconciliationRun = lazy(() =>
  import('./pages/ReconciliationRun.js').then((m) => ({ default: m.ReconciliationRun }))
);
const RetroactiveGlReplay = lazy(() =>
  import('./pages/RetroactiveGlReplay.js').then((m) => ({ default: m.RetroactiveGlReplay }))
);
const AdminEntryCorrections = lazy(() =>
  import('./pages/AdminEntryCorrections.js').then((m) => ({ default: m.AdminEntryCorrections }))
);
const CrossAttestConvention = lazy(() =>
  import('./pages/CrossAttestConvention.js').then((m) => ({ default: m.CrossAttestConvention }))
);

/**
 * Loading fallback for lazy-loaded routes
 */
function PageLoader() {
  return (
    <div className="page-loader" role="status" aria-live="polite">
      <div className="loading-spinner" aria-hidden="true" />
      <p>Loading page...</p>
    </div>
  );
}

/**
 * Derive up-to-2-char initials for the top-bar avatar from the display name.
 */
function getInitials(name?: string | null): string {
  if (!name) return '··';
  return (
    name
      .split(' ')
      .map((part) => part[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2) || '··'
  );
}

/**
 * Authenticated shell — the locked suite chrome (RenewalTopBar + ink AppSidebar)
 * wrapping the routed <Outlet/>. Full-viewport h-svh column with scroll
 * containment: the top bar and sidebar stay put; only <main> scrolls. The nav
 * (labels/order/paths) and its data-testids are preserved from the old top bar.
 */
function AppLayout() {
  const { user, logout, isSupervisor } = useAuth();
  const { count: pendingReviewCount } = useReviewCount();

  return (
    <div className="ri-app-shell flex h-svh flex-col overflow-hidden bg-[var(--ri-bg)]">
      {/* Skip link for keyboard users */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <RenewalTopBar
        current="timesheets"
        user={{ initials: getInitials(user?.name), name: user?.name }}
        showSearch={false}
      />
      <div className="flex flex-1 overflow-hidden">
        <AppSidebar
          user={user}
          isSupervisor={isSupervisor}
          pendingReviewCount={pendingReviewCount}
          onSignOut={logout}
        />
        <main
          id="main-content"
          className="app-main flex-1 min-w-0 overflow-auto overscroll-contain"
          role="main"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Protected route wrapper
 */
function ProtectedRoute({ requireSupervisor = false }: { requireSupervisor?: boolean }) {
  const { isAuthenticated, isSupervisor, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen" role="status" aria-live="polite">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    // Store current path so Login can redirect back after authentication
    const currentPath = window.location.pathname;
    if (currentPath !== '/login' && currentPath !== '/callback') {
      sessionStorage.setItem('returnTo', currentPath);
    }
    return <Navigate to="/login" replace />;
  }

  if (requireSupervisor && !isSupervisor) {
    return (
      <div className="forbidden-screen" role="alert">
        <h1>Access Denied</h1>
        <p>You need supervisor privileges to access this page.</p>
        <Link to="/timesheet">Go to Timesheets</Link>
      </div>
    );
  }

  return <Outlet />;
}

/**
 * Conditional root redirect — admins land on /dashboard, everyone else on
 * /timesheet (Phase 9a Task 1). Lives inside the ProtectedRoute tree so we
 * already know the user is authenticated.
 */
function RootRedirect() {
  const { isSupervisor, loading } = useAuth();
  if (loading) {
    return (
      <div className="loading-screen" role="status" aria-live="polite">
        Loading...
      </div>
    );
  }
  return <Navigate to={isSupervisor ? '/dashboard' : '/timesheet'} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ReviewCountProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/callback" element={<Callback />} />

              {/* Kiosk subtree — anonymous, no AuthProvider, no AppLayout (Phase 7).
                  Mounted OUTSIDE the ProtectedRoute tree so anonymous browsers
                  reach /kiosk without redirect to /login. */}
              <Route path="/kiosk" element={<KioskApp />}>
                <Route index element={<KioskHome />} />
                <Route path="email" element={<KioskEmail />} />
                <Route path="name" element={<KioskName />} />
                <Route path="waiver" element={<KioskWaiver />} />
                <Route path="checkin" element={<KioskCheckin />} />
                <Route path="checkout" element={<KioskCheckout />} />
              </Route>

              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<AppLayout />}>
                  {/* Conditional root redirect — admins land on /dashboard,
                      volunteers (and youth employees) land on /timesheet. */}
                  <Route path="/" element={<RootRedirect />} />

                  {/* Timesheet - accessible to all authenticated users */}
                  <Route path="/timesheet" element={<Timesheet />} />
                  <Route path="/timesheet/:weekStartDate" element={<Timesheet />} />

                  {/* Employee reports - accessible to all authenticated users */}
                  <Route path="/my-reports" element={<MyReportsDashboard />} />
                  <Route path="/my-reports/timesheet-history" element={<MyTimesheetHistory />} />

                  {/* Volunteer self-entry (Phase 4 — R1) — any authenticated user */}
                  <Route path="/my-volunteer-hours" element={<MyVolunteerHours />} />
                  {/* Volunteer week-grid self-entry — the slick drag-to-create surface */}
                  <Route path="/my-volunteer-hours/week" element={<MyVolunteerTimesheet />} />
                  <Route
                    path="/my-volunteer-hours/week/:weekStartDate"
                    element={<MyVolunteerTimesheet />}
                  />

                  {/* Reports dashboard — accessible to all authenticated users.
                      Individual sub-reports (payroll, compliance, etc.) remain
                      supervisor-gated below; non-supervisors clicking those cards
                      hit the Access Denied screen by design. */}
                  <Route path="/reports" element={<ReportsDashboard />} />

                  {/* Supervisor-only routes (lazy loaded) */}
                  <Route element={<ProtectedRoute requireSupervisor />}>
                    <Route path="/employees" element={<EmployeeList />} />
                    <Route path="/employees/:id" element={<EmployeeDetail />} />
                    <Route path="/task-codes" element={<TaskCodeList />} />
                    <Route path="/task-codes/new" element={<TaskCodeForm mode="create" />} />
                    <Route path="/task-codes/:id" element={<TaskCodeDetail />} />
                    <Route path="/task-codes/:id/edit" element={<TaskCodeForm mode="edit" />} />
                    {/* Rate audit detail + refresh — drilled into from a task code,
                        not a standalone Rates section (no list/create routes). */}
                    <Route path="/rates/:id/refresh" element={<RateRefreshWizard />} />
                    <Route path="/rates/:id" element={<RateDetail />} />
                    <Route path="/volunteers" element={<VolunteerList />} />
                    <Route path="/volunteers/onboard" element={<OnboardVolunteerWizard />} />
                    {/* Phase 4 admin tools — paths above the :id wildcard. */}
                    <Route path="/volunteers/transcribe" element={<SigninSheetTranscribe />} />
                    <Route path="/volunteers/signin-template" element={<SigninSheetTemplate />} />
                    {/* Phase 7 kiosk admin tools — also above the :id wildcard. */}
                    <Route path="/volunteers/waivers" element={<KioskWaivers />} />
                    <Route path="/volunteers/kiosk-pending" element={<KioskPendingCompletion />} />
                    <Route path="/volunteers/:id" element={<VolunteerDetail />} />
                    <Route path="/volunteers/:id/edit" element={<VolunteerForm />} />
                    <Route path="/review" element={<ReviewQueue />} />
                    <Route path="/review/:timesheetId" element={<ReviewDetail />} />
                    <Route path="/volunteer-approval" element={<VolunteerApprovalQueue />} />
                    <Route
                      path="/volunteer-approval/:entryId"
                      element={<VolunteerApprovalDetail />}
                    />
                    <Route path="/grants" element={<GrantsList />} />
                    <Route path="/grants/setup" element={<GrantSetupWizard />} />
                    <Route path="/grants/:id" element={<GrantDetail />} />
                    <Route path="/match-bank" element={<MatchBank />} />
                    <Route path="/admin/gl-push" element={<VolunteerGlPush />} />
                    <Route path="/admin/cip-transfer" element={<CipTransfer />} />
                    <Route
                      path="/admin/projects/:projectId/cip-transfer"
                      element={<CipTransfer />}
                    />
                    {/* Phase 11c — GL reconciliation */}
                    <Route path="/admin/reconciliation" element={<Reconciliation />} />
                    <Route
                      path="/admin/reconciliation/runs/:runId"
                      element={<ReconciliationRun />}
                    />
                    <Route path="/admin/gl-replay" element={<RetroactiveGlReplay />} />
                    <Route path="/admin/entry-corrections" element={<AdminEntryCorrections />} />
                    <Route
                      path="/admin/cross-attest-convention"
                      element={<CrossAttestConvention />}
                    />
                    {/* Phase 8 admin tools */}
                    <Route path="/audit-log" element={<AuditLog />} />
                    <Route path="/fiscal-periods" element={<FiscalPeriods />} />
                    <Route path="/fiscal-periods/:id/close" element={<FiscalPeriodCloseWizard />} />
                    <Route
                      path="/reports/match-bank-summary"
                      element={<MatchBankSummaryReport />}
                    />
                    {/*
                      Intentionally UNLINKED from navigation — do not re-add a card or nav
                      entry. Payroll cost is Gusto's (D-001 / D-004, this phase), not this
                      app's. Route kept so bookmarks resolve; aliased at /payroll below;
                      full rationale in ReportsDashboard.tsx.
                    */}
                    <Route path="/reports/payroll" element={<PayrollReportPage />} />
                    <Route path="/reports/compliance-audit" element={<ComplianceAuditReport />} />
                    <Route path="/reports/timesheet-history" element={<TimesheetHistoryReport />} />
                    {/* Phase 9a — admin dashboard + reports */}
                    <Route path="/dashboard" element={<AdminDashboard />} />
                    <Route path="/reports/asu-2020-07" element={<Asu202007Report />} />
                    <Route path="/reports/rate-refresh-log" element={<RateRefreshLogReport />} />
                    <Route path="/reports/audit-packages" element={<AuditPackagesReport />} />
                    {/* Legacy alias for the same intentionally-unlinked payroll report —
                        see the note on /reports/payroll above (D-001 / D-004). */}
                    <Route path="/payroll" element={<PayrollReportPage />} />
                    <Route path="/alerts" element={<Alerts />} />
                  </Route>
                </Route>
              </Route>

              {/* 404 */}
              <Route
                path="*"
                element={
                  <div className="not-found">
                    <h1>Page Not Found</h1>
                    <Link to="/timesheet">Go to Timesheets</Link>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </ReviewCountProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
